// React component

import { motion, AnimatePresence } from "framer-motion";
import { Brain, Sparkles, MessageSquare, Cpu } from "lucide-react";
import { useState, useEffect } from "react";

interface AIProcess {
  id: number;
  icon: React.ElementType;
  label: string;
  status: string;
  progress: number;
  description?: string;
  capabilities?: string[];
}

interface AIMetric {
  label: string;
  value: string;
  icon: React.ElementType;
}

const tabs: {
  id: "processes" | "metrics" | "capabilities";
  label: string;
  helper: string;
  icon: React.ElementType;
}[] = [
  {
    id: "processes",
    label: "AI Pipelines",
    helper: "Real-time training activity",
    icon: Sparkles,
  },
  {
    id: "metrics",
    label: "Performance",
    helper: "Latency & accuracy snapshots",
    icon: Cpu,
  },
  {
    id: "capabilities",
    label: "Capabilities",
    helper: "What each model excels at",
    icon: MessageSquare,
  },
];

const highlightStats = [
  { label: "Active Pipelines", value: "04", icon: Sparkles },
  { label: "Avg Accuracy", value: "99.8%", icon: Brain },
  { label: "Latency", value: "50ms", icon: Cpu },
  { label: "Languages", value: "95+", icon: MessageSquare },
];

const AISolutions = () => {
  const [activeTab, setActiveTab] = useState<
    "processes" | "metrics" | "capabilities"
  >("processes");
  const [processes, setProcesses] = useState<AIProcess[]>([
    {
      id: 1,
      icon: Brain,
      label: "Neural Network Training",
      status: "Processing data...",
      progress: 0,
      description:
        "Advanced neural networks that learn and adapt to your specific needs",
      capabilities: [
        "Pattern Recognition",
        "Adaptive Learning",
        "Real-time Processing",
      ],
    },
    {
      id: 2,
      icon: MessageSquare,
      label: "Language Processing",
      status: "Analyzing patterns...",
      progress: 0,
      description:
        "Natural language understanding and generation for human-like interactions",
      capabilities: [
        "Sentiment Analysis",
        "Content Generation",
        "Multi-language Support",
      ],
    },
    {
      id: 3,
      icon: Sparkles,
      label: "Pattern Recognition",
      status: "Learning patterns...",
      progress: 0,
      description: "Identify complex patterns and trends in your data",
      capabilities: [
        "Trend Analysis",
        "Anomaly Detection",
        "Predictive Insights",
      ],
    },
    {
      id: 4,
      icon: Cpu,
      label: "Machine Learning Pipeline",
      status: "Optimizing models...",
      progress: 0,
      description: "End-to-end ML pipeline for continuous improvement",
      capabilities: [
        "Automated Training",
        "Model Optimization",
        "Performance Monitoring",
      ],
    },
  ]);

  const metrics: AIMetric[] = [
    { label: "Model Accuracy", value: "99.8%", icon: Brain },
    { label: "Processing Speed", value: "50ms", icon: Cpu },
    { label: "Languages Supported", value: "95+", icon: MessageSquare },
    { label: "Pattern Recognition Rate", value: "98.5%", icon: Sparkles },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setProcesses((prev) =>
        prev.map((process) => ({
          ...process,
          progress: Math.min(100, process.progress + Math.random() * 10),
        })),
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="relative flex flex-col gap-4 sm:gap-6 h-full min-h-[230px] sm:min-h-[270px] md:min-h-[320px] lg:min-h-[360px] -mt-1"
      data-oid="n6n8my0"
    >
      {/* Header */}
      <div
        className="rounded-2xl border border-border/60 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 p-4 sm:p-5 shadow-[0_10px_50px_-35px_rgb(59,130,246)]"
        data-oid="hero_header"
      >
        <div className="flex flex-col gap-3" data-oid="header_content">
          <div className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-blue-500"
            data-oid="header_badge"
          >
            <span className="inline-flex size-2 rounded-full bg-blue-500 animate-pulse" data-oid="badge_dot" />
            Live AI Control Center
          </div>
          <div className="flex flex-col gap-2" data-oid="header_text">
            <h3 className="text-base sm:text-lg font-semibold text-foreground" data-oid="header_title">
              Coordinate training, monitoring, and delivery from one widget.
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground" data-oid="header_desc">
              Instantly preview what your automation stack is doing inside the hero without leaving the page.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-oid="header_stats">
            {highlightStats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/60 px-3 py-2"
                data-oid="stat"
              >
                <stat.icon className="h-4 w-4 text-blue-500" data-oid="stat_icon" />
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground" data-oid="stat_label">
                    {stat.label}
                  </p>
                  <p className="text-sm font-semibold" data-oid="stat_value">
                    {stat.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2" data-oid=":4dfy5c">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex min-w-[180px] flex-1 items-center gap-3 rounded-2xl border px-3 py-3 text-left transition-all sm:min-w-[200px] ${
                isActive
                  ? "border-blue-500/60 bg-blue-500/10 shadow-[0_10px_35px_-25px_rgb(59,130,246)]"
                  : "border-border/70 bg-background/60 hover:border-border"
              }`}
              data-oid={`tab_${tab.id}`}
            >
              <tab.icon
                className={`h-4 w-4 ${isActive ? "text-blue-500" : "text-muted-foreground"}`}
                data-oid="tab_icon"
              />
              <div className="flex flex-col">
                <span
                  className={`text-xs font-semibold sm:text-sm ${
                    isActive ? "text-foreground" : "text-foreground/80"
                  }`}
                  data-oid="tab_label"
                >
                  {tab.label}
                </span>
                <span className="text-[11px] text-muted-foreground" data-oid="tab_helper">
                  {tab.helper}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div
        className="flex-1 overflow-hidden rounded-2xl border border-border/60 bg-background/80"
        data-oid="content_shell"
      >
        <div
          className="flex-1 overflow-y-auto px-1 sm:px-3 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent pb-2"
          data-oid="2czjoqb"
        >
          <AnimatePresence mode="wait" data-oid=":m7qxdb">
          {activeTab === "processes" && (
            <motion.div
              key="processes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-3 sm:space-y-4 pb-4"
              data-oid="5vybgyu"
            >
              {processes.map((process) => (
                <motion.article
                  key={process.id}
                  className="rounded-2xl border border-border/70 bg-card/60 p-3 sm:p-4 shadow-[0_20px_45px_-40px_rgb(59,130,246)]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  whileHover={{ translateY: -2 }}
                  data-oid="puut3f8"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3" data-oid="process_header">
                    <div className="flex items-center gap-3" data-oid="process_lead">
                      <div className="flex size-10 items-center justify-center rounded-xl bg-blue-500/10" data-oid="process_icon">
                        <process.icon className="h-4 w-4 text-blue-500" data-oid="r.u.jbo" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold" data-oid="q8:jftd">
                          {process.label}
                        </h3>
                        <p className="text-xs text-muted-foreground" data-oid="di5deid">
                          {process.status}
                        </p>
                      </div>
                    </div>
                    <span
                      className="inline-flex items-center rounded-full border border-blue-500/40 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold text-blue-500"
                      data-oid="6n-i9vk"
                    >
                      {Math.round(process.progress)}% Complete
                    </span>
                  </div>

                  <p className="mt-3 text-xs sm:text-sm text-muted-foreground" data-oid="eq_e:e-">
                    {process.description}
                  </p>

                  <div className="mt-3 space-y-3" data-oid="process_progress">
                    <div className="h-1.5 rounded-full bg-blue-500/15" data-oid="hv4oxc1">
                      <motion.div
                        className="h-full rounded-full bg-blue-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${process.progress}%` }}
                        transition={{ duration: 0.5 }}
                        data-oid="g2g4-0v"
                      />
                    </div>

                    {process.capabilities && (
                      <div className="flex flex-wrap gap-2" data-oid="process_caps">
                        {process.capabilities.map((capability, index) => (
                          <span
                            key={`${process.id}-${capability}-${index}`}
                            className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/80 px-2 py-1 text-[11px] text-muted-foreground"
                            data-oid="capability_chip"
                          >
                            <span className="size-1.5 rounded-full bg-blue-500" data-oid="capability_marker" />
                            {capability}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between" data-oid="p9_aba1">
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="h-1.5 w-1.5 rounded-full bg-blue-500"
                          animate={{
                            opacity: [0.2, 1, 0.2],
                            scale: [1, 1.4, 1],
                          }}
                          transition={{
                            duration: 1.5,
                            delay: i * 0.2,
                            repeat: Infinity,
                          }}
                          data-oid="0608wge"
                        />
                      ))}
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}

          {activeTab === "metrics" && (
            <motion.div
              key="metrics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 gap-3 pb-4 sm:grid-cols-2"
              data-oid="s-2_e.:"
            >
              {metrics.map((metric, index) => (
                <motion.div
                  key={metric.label}
                  className="rounded-2xl border border-border/70 bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-3 sm:p-5"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: index * 0.1 },
                  }}
                  whileHover={{ translateY: -3 }}
                  data-oid="zknlzlu"
                >
                  <div className="flex items-center justify-between gap-3" data-oid="metric_header">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-xl bg-background/60" data-oid="metric_icon">
                        <metric.icon className="h-5 w-5 text-blue-500" data-oid="8lau:qv" />
                      </div>
                      <div data-oid="0ne6j48">
                        <h3 className="text-sm font-semibold" data-oid="ltsu015">
                          {metric.label}
                        </h3>
                        <p className="text-xs text-muted-foreground" data-oid="metric_hint">
                          Updated moments ago
                        </p>
                      </div>
                    </div>
                    <span className="text-lg sm:text-2xl font-bold text-blue-500" data-oid="65clm4k">
                      {metric.value}
                    </span>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground" data-oid="metric_desc">
                    Performance snapshot from the live experimentation cluster.
                  </p>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "capabilities" && (
            <motion.div
              key="capabilities"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 gap-3 pb-4 sm:grid-cols-2"
              data-oid=":9y.ahs"
            >
              {processes.map((process) => (
                <motion.article
                  key={process.id}
                  className="rounded-2xl border border-border/70 bg-card/60 p-3 sm:p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ translateY: -2 }}
                  data-oid=":1v6-kc"
                >
                  <div className="flex items-start gap-3" data-oid="cap_header">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-blue-500/10" data-oid="cap_icon">
                      <process.icon className="h-4 w-4 text-blue-500" data-oid="-mtdq0r" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold" data-oid="2w74.hy">
                        {process.label}
                      </h3>
                      <p className="text-xs text-muted-foreground" data-oid="cap_sub">
                        Core strengths inside this workflow.
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2" data-oid="ehfrbce">
                    {process.capabilities?.map((capability, index) => (
                      <motion.div
                        key={index}
                        className="flex items-start gap-2 rounded-xl border border-border/60 bg-background/70 px-3 py-2 text-[11px] sm:text-xs text-muted-foreground"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          transition: { delay: index * 0.07 },
                        }}
                        data-oid="52rwm.b"
                      >
                        <div
                          className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-500"
                          data-oid="iyy50eg"
                        />

                        {capability}
                      </motion.div>
                    ))}
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
};

export default AISolutions;
